import { type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare } from 'lucide-react';
import type { FeedbackSection } from '../../types';
import { useFeedback } from '../../context';
import { useProposalData } from '../../context';

interface WithFeedbackProps {
  children: ReactNode;
  section: FeedbackSection;
  lineNumber?: number;
  position?: 'top-right' | 'bottom-right' | 'inline';
  className?: string;
}

/**
 * HOC-style component that wraps content with a feedback button
 */
export function WithFeedback({
  children,
  section,
  lineNumber,
  position = 'top-right',
  className = '',
}: WithFeedbackProps) {
  const { openFeedbackModal, getFeedbackCount } = useFeedback();
  const proposal = useProposalData();
  const count = getFeedbackCount(section, lineNumber);

  if (!proposal.feedbackEnabled) {
    return <>{children}</>;
  }

  const buttonPositionClasses = {
    'top-right': 'absolute top-4 right-4',
    'bottom-right': 'absolute bottom-4 right-4',
    inline: 'ml-auto',
  };

  const FeedbackTrigger = () => (
    <motion.button
      onClick={() => openFeedbackModal(section, lineNumber)}
      className={`${
        position !== 'inline' ? buttonPositionClasses[position] : ''
      } flex items-center gap-1.5 px-3 py-1.5 bg-white/90 backdrop-blur-sm text-gray-600 hover:text-hul-red text-sm rounded-lg shadow-sm border border-gray-200 hover:border-hul-red/30 transition-all z-10`}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <MessageSquare className="w-4 h-4" />
      <span className="hidden sm:inline">Feedback</span>
      {count > 0 && (
        <span className="w-5 h-5 bg-hul-orange text-white text-xs font-bold rounded-full flex items-center justify-center">
          {count}
        </span>
      )}
    </motion.button>
  );

  if (position === 'inline') {
    return (
      <div className={`flex items-center ${className}`}>
        {children}
        <FeedbackTrigger />
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <FeedbackTrigger />
      {children}
    </div>
  );
}
